/**
 * Multi-select picker with type-ahead filtering and optional bulk
 * "show all" / "hide all" buttons. Mirrors the Logic Apps
 * `searchabledropdownWithAddAll` pattern on top of Fluent UI v9.
 *
 * Fully controlled — callers own `selectedKeys` and react to
 * `onSelectedKeysChange` / bulk callbacks so they can perform side effects
 * such as clearing an underlying value when an option is removed.
 */

import {
  Button,
  Combobox,
  Field,
  Option,
  Tooltip,
  makeStyles,
  tokens,
  useId,
} from '@coreai/fluentui-react';
import type {
  ComboboxProps,
  OptionOnSelectData,
  SelectionEvents,
} from '@coreai/fluentui-react';
import { useCallback, useMemo, useState, type ChangeEvent, type FC, type ReactNode } from 'react';

export interface SearchableDropdownOption {
  value: string;
  label: string;
  /** Shown but not toggleable. */
  disabled?: boolean;
}

export interface SearchableDropdownWithAddAllProps {
  label?: string;
  placeholder?: string;
  hint?: string;
  options: SearchableDropdownOption[];
  selectedKeys: string[];
  onSelectedKeysChange: (keys: string[]) => void;
  disabled?: boolean;

  addAllButtonText?: string;
  addAllButtonTooltip?: string;
  addAllButtonEnabled?: boolean;
  onShowAllClick?: () => void;

  removeAllButtonText?: string;
  removeAllButtonTooltip?: string;
  removeAllButtonEnabled?: boolean;
  onHideAllClick?: () => void;
}

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  combobox: {
    flex: 1,
    minWidth: 0,
  },
  buttonGroup: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
});

interface BulkActionButtonProps {
  text: string;
  tooltip?: string;
  disabled: boolean;
  onClick: () => void;
}

const BulkActionButton: FC<BulkActionButtonProps> = ({ text, tooltip, disabled, onClick }) => {
  const button = (
    <Button size="small" appearance="outline" onClick={onClick} disabled={disabled}>
      {text}
    </Button>
  );
  if (!tooltip) return button;
  return (
    <Tooltip content={tooltip} relationship="label" withArrow>
      {button}
    </Tooltip>
  );
};

export const SearchableDropdownWithAddAll: FC<SearchableDropdownWithAddAllProps> = ({
  label,
  placeholder,
  hint,
  options,
  selectedKeys,
  onSelectedKeysChange,
  disabled = false,
  addAllButtonText,
  addAllButtonTooltip,
  addAllButtonEnabled = true,
  onShowAllClick,
  removeAllButtonText,
  removeAllButtonTooltip,
  removeAllButtonEnabled = true,
  onHideAllClick,
}) => {
  const styles = useStyles();
  const comboboxId = useId('searchable-dropdown');
  const [query, setQuery] = useState('');

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(needle));
  }, [options, query]);

  const displayValue = useMemo(() => {
    if (selectedKeys.length === 0) return '';
    const labelByValue = new Map(options.map((opt) => [opt.value, opt.label]));
    return selectedKeys.map((key) => labelByValue.get(key) ?? key).join(', ');
  }, [options, selectedKeys]);

  const disabledKeys = useMemo(
    () => new Set(options.filter((opt) => opt.disabled).map((opt) => opt.value)),
    [options],
  );

  const handleOptionSelect = useCallback<NonNullable<ComboboxProps['onOptionSelect']>>(
    (_event: SelectionEvents, data: OptionOnSelectData) => {
      // Preserve disabled selections — callers can't drop locked items by toggling.
      const next = new Set(data.selectedOptions);
      for (const key of selectedKeys) {
        if (disabledKeys.has(key)) next.add(key);
      }
      onSelectedKeysChange(Array.from(next));
      setQuery('');
    },
    [disabledKeys, onSelectedKeysChange, selectedKeys],
  );

  const handleInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, []);

  const renderOptions = (): ReactNode => {
    if (filteredOptions.length === 0) {
      return (
        <Option key="__no_results" value="__no_results" disabled>
          No matching options
        </Option>
      );
    }
    return filteredOptions.map((opt) => (
      <Option
        key={opt.value}
        value={opt.value}
        text={opt.label}
        {...(opt.disabled ? { disabled: true } : {})}
      >
        {opt.label}
      </Option>
    ));
  };

  const showButtons = Boolean(
    (onShowAllClick && addAllButtonText) || (onHideAllClick && removeAllButtonText),
  );

  return (
    <Field
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
    >
      <div className={styles.row}>
        <Combobox
          id={comboboxId}
          multiselect
          freeform={false}
          className={styles.combobox}
          {...(placeholder !== undefined ? { placeholder } : {})}
          disabled={disabled}
          selectedOptions={selectedKeys}
          value={query.length > 0 ? query : displayValue}
          onOptionSelect={handleOptionSelect}
          onInput={handleInput}
          onBlur={() => setQuery('')}
        >
          {renderOptions()}
        </Combobox>
        {showButtons && (
          <div className={styles.buttonGroup}>
            {onShowAllClick && addAllButtonText && (
              <BulkActionButton
                text={addAllButtonText}
                {...(addAllButtonTooltip !== undefined ? { tooltip: addAllButtonTooltip } : {})}
                disabled={disabled || !addAllButtonEnabled}
                onClick={onShowAllClick}
              />
            )}
            {onHideAllClick && removeAllButtonText && (
              <BulkActionButton
                text={removeAllButtonText}
                {...(removeAllButtonTooltip !== undefined ? { tooltip: removeAllButtonTooltip } : {})}
                disabled={disabled || !removeAllButtonEnabled}
                onClick={onHideAllClick}
              />
            )}
          </div>
        )}
      </div>
    </Field>
  );
};

SearchableDropdownWithAddAll.displayName = 'SearchableDropdownWithAddAll';
